import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  CalendarCheck, ListTodo, FilePlus2, Table2, LayoutDashboard, BarChart3, History, Columns3, Building2,
  Settings as SettingsIcon, Upload, Moon, Sun, LogOut, Menu, X, Flame,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { useCustomer } from "@/lib/customer";

const NAV = [
  { to: "/my-day", label: "My day", icon: CalendarCheck, testId: "nav-my-day" },
  { to: "/board", label: "Board", icon: Columns3, testId: "nav-board" },
  { to: "/tasks", label: "Follow-ups", icon: ListTodo, testId: "nav-tasks" },
  { to: "/po-new", label: "New PO", icon: FilePlus2, testId: "nav-new-po", writes: true },
  { to: "/pos", label: "All POs", icon: Table2, testId: "nav-all-pos" },
  { to: "/po-history", label: "PO history", icon: History, testId: "nav-po-history" },
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/reports", label: "Reports", icon: BarChart3, testId: "nav-reports" },
  { to: "/import", label: "Import", icon: Upload, testId: "nav-import", writes: true },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testId: "nav-settings", managerOnly: true, writes: true },
  { to: "/customers", label: "Customers", icon: Building2, testId: "nav-customers", adminOnly: true },
];

export default function Layout() {
  const { user, logout, isManager, isAdmin } = useAuth();
  const { customers, canWorkAcross, customerId, pick, isAllCustomers } = useCustomer();
  const { theme, toggle } = useTheme();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const items = NAV.filter(
    (item) =>
      (!item.managerOnly || isManager) &&
      (!item.adminOnly || isAdmin) &&
      !(isAllCustomers && item.writes)
  );

  const linkClass = ({ isActive }) =>
    `flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors ${
      isActive
        ? "bg-primary text-primary-foreground"
        : "text-muted-foreground hover:bg-secondary hover:text-foreground"
    }`;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border bg-card/90 backdrop-blur">
        <div className="flex items-center gap-3 px-3 py-2 sm:px-6">
          <button
            className="rounded-md p-2 hover:bg-secondary lg:hidden"
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-toggle-button"
            aria-label="Toggle navigation"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
          <button
            onClick={() => navigate("/my-day")}
            className="flex items-center gap-2"
            data-testid="brand-home-button"
          >
            <Flame className="h-5 w-5 text-[#004B87] dark:text-sky-400" />
            <span className="text-sm font-semibold tracking-tight">Kanex PO tracking</span>
          </button>

          {customers.length > 0 && (
            <select
              value={customerId}
              onChange={(e) => pick(e.target.value)}
              className="h-8 max-w-[180px] rounded-md border border-input bg-background px-2 text-xs font-medium"
              data-testid="customer-picker"
              aria-label="Customer"
            >
              {customers.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
              {canWorkAcross && <option value="__all">All customers</option>}
            </select>
          )}

          <nav className="ml-4 hidden items-center gap-1 lg:flex">
            {items.map(({ to, label, icon: Icon, testId }) => (
              <NavLink key={to} to={to} className={linkClass} data-testid={testId}>
                <Icon className="h-4 w-4" />
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <div className="hidden text-right sm:block">
              <div className="text-xs font-medium leading-tight" data-testid="current-user-name">{user?.name}</div>
              <div className="text-[11px] leading-tight text-muted-foreground">{user?.role}</div>
            </div>
            <button
              onClick={toggle}
              className="rounded-md p-2 hover:bg-secondary"
              data-testid="theme-toggle-button"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <button
              onClick={logout}
              className="rounded-md p-2 hover:bg-secondary"
              data-testid="logout-button"
              aria-label="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>

        {open && (
          <nav className="grid gap-1 border-t border-border px-3 py-2 lg:hidden" data-testid="mobile-nav">
            {items.map(({ to, label, icon: Icon, testId }) => (
              <NavLink
                key={to}
                to={to}
                className={linkClass}
                onClick={() => setOpen(false)}
                data-testid={`${testId}-mobile`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </NavLink>
            ))}
          </nav>
        )}
      </header>

      <main className="mx-auto w-full max-w-[1600px] p-3 sm:p-6">
        {isAllCustomers && (
          <div
            className="mb-4 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300"
            data-testid="all-customers-readonly-banner"
          >
            Every customer at once. This view is read-only — pick one customer to record anything.
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
}
